import moment, {Moment} from "moment";
import {DateFormats} from "../components/constants/securspace-constants";
import {parseQueryParamDay} from "./DateUtils";

export interface DateRange {
  startDate: string;
  endDate: string;
}

/** A date filter change: the dates that changed and/or the apply-dates override, applied together. */
export type DateRangeChange = Partial<DateRange> & { applyDates?: boolean };

export type DateRangePresetKey = 'PAST_MONTH' | 'PAST_3_MONTHS' | 'PAST_YEAR' | 'YEAR_TO_DATE' | 'ALL_DATES' | 'CUSTOM';

interface DateRangePreset {
  key: Exclude<DateRangePresetKey, 'CUSTOM'>;
  label: string;
  getRange: (today: Moment) => DateRange;
}

/** URL/query flag that keeps the date range applied while searching by an ID. */
export const APPLY_DATES_PARAM = 'applyDates';

/** A date bound read from the URL: '' when present but blank (All Dates), undefined when missing or malformed. */
export const parseDateRangeParam = (value?: string | null): string | undefined =>
  value === '' ? '' : parseQueryParamDay(value);

/**
 * The range a report opens on: URL dates where given, else exactly its defaults (which is what
 * usePersistDateRangeParams omits). Only a link carrying a start but no end gets `endFromStart`.
 */
export const resolveInitialDateRange = (
  url: Partial<DateRange>,
  defaults: DateRange,
  endFromStart?: (startDate: string) => string,
): DateRange => ({
  startDate: url.startDate ?? defaults.startDate,
  endDate: url.endDate ?? (url.startDate && endFromStart ? endFromStart(url.startDate) : defaults.endDate),
});

const formatDay = (date: Moment) => date.format(DateFormats.DAY);

const lookBack = (amount: number, unit: 'month' | 'year') => (today: Moment): DateRange => ({
  startDate: formatDay(today.clone().subtract(amount, unit)),
  endDate: formatDay(today),
});

export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  {key: 'PAST_MONTH', label: 'Past Month', getRange: lookBack(1, 'month')},
  {key: 'PAST_3_MONTHS', label: 'Past 3 Months', getRange: lookBack(3, 'month')},
  {key: 'PAST_YEAR', label: 'Past Year', getRange: lookBack(1, 'year')},
  {
    key: 'YEAR_TO_DATE',
    label: 'Year to Date',
    getRange: (today) => ({startDate: formatDay(today.clone().startOf('year')), endDate: formatDay(today)}),
  },
  {key: 'ALL_DATES', label: 'All Dates', getRange: () => ({startDate: '', endDate: ''})},
];

export const getDateRangeForPreset = (key: DateRangePresetKey, today: Moment = moment()): DateRange | undefined =>
  DATE_RANGE_PRESETS.find(preset => preset.key === key)?.getRange(today);

// Legacy filters hold ISO strings, bound reports hold MM/DD/YYYY.
const toDay = (value?: string | null): string => {
  if (!value) return '';
  const parsed = moment(value, [DateFormats.DAY, moment.ISO_8601], true);
  return parsed.isValid() ? formatDay(parsed) : value;
};

export const findDateRangePreset = (
  startDate?: string | null,
  endDate?: string | null,
  {startOnly = false, today = moment()}: { startOnly?: boolean, today?: Moment } = {},
): DateRangePresetKey => {
  const start = toDay(startDate);
  const end = toDay(endDate);
  const match = DATE_RANGE_PRESETS.find(preset => {
    const range = preset.getRange(today);
    return range.startDate === start && (startOnly || range.endDate === end);
  });
  return match?.key ?? 'CUSTOM';
};

export const hasIdSearch = (query: object, idKeys: readonly string[]): boolean => {
  const values = query as Record<string, unknown>;
  return idKeys.some(key => {
    const value = values[key];
    return value !== null && value !== undefined && String(value).trim() !== '';
  });
};

export const isApplyDates = (query: object): boolean => {
  const value = (query as Record<string, unknown>)[APPLY_DATES_PARAM];
  return value === true || value === 'true';
};

export const shouldIgnoreDateRange = (query: object, idKeys: readonly string[]): boolean =>
  hasIdSearch(query, idKeys) && !isApplyDates(query);

/** Query keys a report's date range is stored under when it uses the default `startDate`/`endDate` names. */
export const DEFAULT_DATE_RANGE_KEYS: readonly string[] = ['startDate', 'endDate'];

/**
 * The query to send to the API: when an ID filter (invoice, booking number...) has a value, the
 * date range is blanked so the record is found however old it is. The API reads blank dates as unset.
 * @param query the report query
 * @param idKeys query keys that search all dates while filled
 * @param dateKeys query keys holding the date bounds to blank (defaults to `startDate`/`endDate`)
 */
export const withDateRangeBypass = <T extends object>(
  query: T,
  idKeys: readonly string[],
  dateKeys: readonly string[] = DEFAULT_DATE_RANGE_KEYS,
): T => {
  const result: Record<string, unknown> = {...(query as Record<string, unknown>)};
  delete result[APPLY_DATES_PARAM];
  if (shouldIgnoreDateRange(query, idKeys)) {
    dateKeys.forEach(key => {
      if (key in result) result[key] = '';
    });
  }
  return result as T;
};
